import { docMetaFrom } from '../dto/documents.js';
import { EXAMPLE_IDS } from './example-ids.js';
import { EXAMPLE_ANSWER_IDS, EXAMPLE_NOW } from './examples.js';
import { EXAMPLE_TYPE_IDS, exampleDocument } from './examples-docs.js';
const ids = EXAMPLE_IDS;
const answer = EXAMPLE_ANSWER_IDS;
/** A homebrew stat block: the Drowned Acolyte, one trait and one action. */
export const EXAMPLE_STATBLOCK = {
    schemaVersion: 1,
    source: { kind: 'form', upstreamKey: null, foundryLayout: null },
    rules: '2014',
    name: 'Drowned Acolyte',
    size: 'med',
    creatureType: { value: 'humanoid', subtype: 'any race', swarmSize: null, custom: null },
    alignment: 'neutral evil',
    armorClass: [{ value: 12, source: 'leather armor' }],
    hitPoints: { average: 27, formula: { count: 6, die: 8, bonus: 0 } },
    hitDice: { count: 6, die: 8 },
    speed: { walk: 30, fly: null, swim: 30, climb: null, burrow: null, hover: false, note: null },
    abilities: { str: 10, dex: 12, con: 10, int: 10, wis: 14, cha: 11 },
    savingThrows: [],
    skills: [{ skill: 'rel', bonus: 2 }],
    damageVulnerabilities: [],
    damageResistances: [],
    damageImmunities: [],
    conditionImmunities: [],
    senses: {
        blindsight: null,
        darkvision: null,
        tremorsense: null,
        truesight: null,
        blindBeyond: false,
        passivePerception: 12,
        note: null,
    },
    languages: { values: ['Common'], telepathy: null, note: null },
    challenge: { rating: 1, xp: 200, lairXp: null },
    proficiencyBonus: 2,
    initiativeBonus: null,
    traits: [
        {
            id: '6f1d2a40-5b3c-5d7e-9f80-1a2b3c4d5e01',
            name: 'Breath of the Deep',
            text: 'The acolyte can breathe air and water.',
            usage: null,
            attack: null,
        },
    ],
    actions: [
        {
            id: '6f1d2a40-5b3c-5d7e-9f80-1a2b3c4d5e02',
            name: 'Barnacled Sickle',
            text: '*Melee Weapon Attack:* +3 to hit, reach 5 ft., one target. *Hit:* 4 (1d4 + 2) slashing damage.',
            usage: null,
            attack: {
                range: 'melee',
                kind: 'weapon',
                toHit: 3,
                reach: 5,
                rangeNormal: null,
                rangeLong: null,
                target: 'one target',
                damage: [
                    {
                        average: 4,
                        dice: { count: 1, die: 4, bonus: 2 },
                        flat: null,
                        type: 'slashing',
                        note: null,
                    },
                ],
                versatile: null,
                riders: [],
                hitText: null,
            },
        },
    ],
    bonusActions: [],
    reactions: [],
    legendary: null,
    legendaryResistance: null,
    lair: null,
    spellcasting: [],
    habitat: [],
    treasure: [],
    state: null,
};
/** The Drowned Acolyte's primary sheet, at version 1. */
export const EXAMPLE_SHEET = {
    id: ids.sheetDrownedAcolyte.id,
    documentId: ids.creatureDrownedAcolyte.id,
    label: null,
    isPrimary: true,
    kind: 'statblock',
    rulesVersion: '2014',
    schemaVersion: 1,
    data: EXAMPLE_STATBLOCK,
    derived: null,
    derivedSource: 'form',
    source: 'form',
    fieldOrigins: {},
    version: 1,
    createdAt: EXAMPLE_NOW,
    updatedAt: EXAMPLE_NOW,
};
const ORLA_DATA = {
    ...EXAMPLE_STATBLOCK,
    name: 'Captain Orla Venn',
    traits: [],
    actions: [],
};
/**
 * The sheet `sheets.create`'s example makes for Captain Orla Venn, as 1.AC-1's `createSheet` writes
 * it (deliverable 3; CS r2c aligns it): the server owns `data.source` (`form`, no upstream key),
 * and every top-level key of `data` is `local` in `fieldOrigins` (`localOrigins`).
 */
export const EXAMPLE_SHEET_CREATED = {
    ...EXAMPLE_SHEET,
    id: answer.sheetCreated,
    documentId: ids.npcOrlaVenn.id,
    label: 'Harbour watch captain',
    data: ORLA_DATA,
    fieldOrigins: Object.fromEntries(Object.keys(ORLA_DATA).map((key) => [key, 'local'])),
};
/** A spell card written fresh for the stand-in of the SRD's *Speak with Dead*. */
export const EXAMPLE_SPELL = {
    schemaVersion: 1,
    source: { kind: 'srd', upstreamKey: 'speak-with-dead', foundryLayout: null },
    rules: '2014',
    name: 'Speak with Dead',
    level: 3,
    school: 'nec',
    ritual: false,
    castingTime: { amount: 1, unit: 'action', reactionTrigger: null },
    range: { kind: 'distance', distance: 10, unit: 'ft', shape: null },
    components: {
        verbal: true,
        somatic: true,
        material: true,
        materialText: 'burning incense',
        materialCost: null,
        materialConsumed: false,
    },
    duration: { amount: 10, unit: 'minute', concentration: false },
    classes: ['bard', 'cleric'],
    attackRoll: false,
    saveAbility: null,
    damage: { roll: null, types: [] },
    target: { type: 'creature', count: 1 },
    text: 'A corpse within range answers up to five questions with what it knew in life.',
    higherLevels: null,
    gmText: null,
    upcast: [],
};
export const EXAMPLE_RULE_CARD = {
    kind: 'spell',
    documentId: ids.referenceSpeakWithDead.id,
    rv: 1,
    schemaVersion: 1,
    data: EXAMPLE_SPELL,
    source: 'srd',
    createdAt: EXAMPLE_NOW,
    updatedAt: EXAMPLE_NOW,
};
/** The SRD Ghoul's reference page and its primary sheet, as the seed writes them (CS r3a). */
export const EXAMPLE_REFERENCE_ENTRY = {
    document: exampleDocument({
        id: ids.referenceGhoul.id,
        fid: 'd2p2N9eWVue5htYG',
        typeId: EXAMPLE_TYPE_IDS.ref_creature,
        typeKey: 'ref_creature',
        title: 'Ghoul',
        body: { type: 'doc', content: [{ type: 'paragraph' }] },
        library: 'reference',
        canonStatus: 'reference',
        aiContext: 'n_a',
        autolink: false,
        playState: 'n_a',
    }),
    kind: 'creature',
    source: {
        key: 'srd-5.1',
        title: 'System Reference Document 5.1',
        licence: 'CC-BY-4.0',
        attribution: 'This work includes material taken from the System Reference Document 5.1 ("SRD 5.1") by Wizards of the Coast LLC.',
    },
    upstreamKey: 'srd_ghoul',
    contentHash: '5a'.repeat(32),
    withdrawnAt: null,
    sheet: {
        ...EXAMPLE_SHEET,
        id: ids.sheetReferenceGhoul.id,
        documentId: ids.referenceGhoul.id,
        source: 'srd',
        data: {
            ...EXAMPLE_STATBLOCK,
            name: 'Ghoul',
            source: { kind: 'srd', upstreamKey: 'srd_ghoul', foundryLayout: null },
        },
    },
    card: null,
};
/** The variant `variants.create`'s example makes: the Drowned Ghoul, a Sea Beast. */
export const EXAMPLE_VARIANT_CREATED = {
    document: docMetaFrom(exampleDocument({
        id: answer.variantCreated,
        fid: 'DrownedGhoul0001',
        typeId: ids.typeSeaBeast.id,
        typeKey: 'sea_beast',
        title: 'Drowned Ghoul',
        body: { type: 'doc', content: [{ type: 'paragraph' }] },
        canonStatus: 'draft',
    }), { capabilities: ['has_sheet'], homeId: ids.locationSunkenChapelCrypt.id }),
    sheetId: answer.variantSheetCreated,
};
/** A write the rows refuse (1.AC-1 deliverable 7): `details.path` and `details.issue` name it. */
const sheetInvalid = (issue, path) => ({
    status: 422,
    body: { code: 'invalid_body', message: 'The sheet cannot be written.', details: { path, issue } },
});
const trashed = {
    status: 409,
    body: { code: 'document_trashed', message: 'The document is in the trash.' },
};
/**
 * The answers CS r2c declared on the sheet writes and the variant flow (1.AC-1's item AC2;
 * DECISIONS 146 item 11), one example each, with a request where a demo row meets the refusal.
 * No SRD row is in the demo yet, so no `variants.create` refusal has a request: its source answers
 * 404 first.
 */
export const EXAMPLE_SHEET_REFUSALS = {
    'variants.create': [
        trashed,
        sheetInvalid('type_without_sheet', ['body', 'typeId']),
        sheetInvalid('unknown_type', ['body', 'typeId']),
        sheetInvalid('source_not_creature', ['params', 'id']),
    ],
    'sheets.create': [
        {
            ...trashed,
            request: { params: { id: ids.noteTornLedger.id }, body: { data: ORLA_DATA } },
        },
        {
            ...sheetInvalid('type_without_sheet', ['params', 'id']),
            request: { params: { id: ids.questSaltInTheWells.id }, body: { data: ORLA_DATA } },
        },
        {
            ...sheetInvalid('rules_2024', ['body', 'data', 'rules']),
            request: {
                params: { id: ids.npcOrlaVenn.id },
                body: { data: { ...ORLA_DATA, rules: '2024' } },
            },
        },
    ],
    'sheets.update': [
        trashed,
        {
            ...sheetInvalid('rules_2024', ['body', 'data', 'rules']),
            request: {
                params: { id: ids.creatureDrownedAcolyte.id, sheetId: ids.sheetDrownedAcolyte.id },
                body: { baseVersion: 1, data: { ...EXAMPLE_STATBLOCK, rules: '2024' } },
            },
        },
        {
            ...sheetInvalid('primary_required', ['body', 'isPrimary']),
            request: {
                params: { id: ids.creatureDrownedAcolyte.id, sheetId: ids.sheetDrownedAcolyte.id },
                body: { baseVersion: 1, isPrimary: false },
            },
        },
    ],
};
// ---- CS r6c deliverable 202: the card writes and the card variant (owner 2.M, stage s1) ----
/**
 * The canon `spell` type's id (CS r6c deliverable 201): `builtinId('doc_type', 'spell')` of
 * `@codex/db`'s seed, `uuidV5('doc_type/spell', NS_CODEX_BUILTIN)`, written as a literal because
 * contracts never imports the database package (`dto-2c-cards.test.ts` recomputes it).
 */
export const EXAMPLE_SPELL_TYPE_ID = 'e33571c4-1a15-5e29-8619-9dc674e60ec4';
/** The Lantern of Warding's homebrew card, as the GM's form sends it (`rules.create`). */
export const EXAMPLE_CARD_ITEM = {
    schemaVersion: 1,
    source: { kind: 'form', upstreamKey: null, foundryLayout: null },
    rules: '2014',
    name: 'Lantern of Warding',
    category: 'wondrous-item',
    rarity: 'uncommon',
    attunement: { required: false, requirement: null },
    weapon: null,
    armor: null,
    object: null,
    weight: 2,
    cost: null,
    text: 'While this hooded lantern burns, undead within its bright light have disadvantage on attack rolls against the one who holds it.',
    gmText: null,
};
/** Brine Ward, a homebrew spell card of `demo@p2c` (CS r6b), as `rules.update` edits it. */
export const EXAMPLE_CARD_SPELL = {
    ...EXAMPLE_SPELL,
    source: { kind: 'form', upstreamKey: null, foundryLayout: null },
    name: 'Brine Ward',
    level: 1,
    school: 'abj',
    castingTime: { amount: 1, unit: 'reaction', reactionTrigger: 'when you are hit while in water' },
    range: { kind: 'self', distance: null, unit: null, shape: null },
    components: { ...EXAMPLE_SPELL.components, material: false, materialText: null },
    duration: { amount: 1, unit: 'round', concentration: false },
    classes: ['sorcerer', 'wizard'],
    text: 'A crust of hardened brine closes over you: your Armor Class rises by 4 until your next turn.',
};
/** `rules.create`'s request: the item card of the Lantern of Warding, from the form. */
export const EXAMPLE_CARD_CREATE = {
    kind: 'item',
    data: EXAMPLE_CARD_ITEM,
    source: 'form',
};
/** `rules.create`'s answer: the new card at `rv` 1, its source the server's (`form`). */
export const EXAMPLE_CARD_CREATED = {
    kind: 'item',
    documentId: ids.itemLanternOfWarding.id,
    rv: 1,
    schemaVersion: 1,
    data: EXAMPLE_CARD_ITEM,
    source: 'form',
    createdAt: EXAMPLE_NOW,
    updatedAt: EXAMPLE_NOW,
};
const brineWardEdited = {
    ...EXAMPLE_CARD_SPELL,
    classes: ['druid', 'sorcerer', 'wizard'],
};
/** `rules.update`'s request: Brine Ward's card at `rv` 1, the druid added to its classes. */
export const EXAMPLE_CARD_UPDATE = { baseRv: 1, data: brineWardEdited };
/** `rules.update`'s answer: the card at `rv` 2, its stored source kept. */
export const EXAMPLE_CARD_UPDATED = {
    kind: 'spell',
    documentId: ids.spellBrineWard.id,
    rv: 2,
    schemaVersion: 1,
    data: brineWardEdited,
    source: 'form',
    createdAt: EXAMPLE_NOW,
    updatedAt: EXAMPLE_NOW,
};
/**
 * The card variant (CS r6c deliverable 202; 2.M s1, ruling 8): `variants.create` from the SRD's
 * *Speak with Dead* with the canon `spell` type answers its new canon page and `sheetId: null`,
 * beside `variants.create`'s own example (a creature), which is unchanged. 2.M s1's dispatch line
 * implements it; this pair is no route example, so it is not run by the parity.
 */
export const EXAMPLE_VARIANT_SPELL = {
    request: {
        params: { id: ids.referenceSpeakWithDead.id },
        body: {
            title: 'Speak with the Drowned',
            typeId: EXAMPLE_SPELL_TYPE_ID,
            homeId: ids.locationSunkenChapelCrypt.id,
        },
    },
    response: {
        document: docMetaFrom(exampleDocument({
            id: answer.variantSpellCreated,
            fid: 'DrownedSpell0001',
            typeId: EXAMPLE_SPELL_TYPE_ID,
            typeKey: 'spell',
            title: 'Speak with the Drowned',
            body: { type: 'doc', content: [{ type: 'paragraph' }] },
            canonStatus: 'draft',
        }), { capabilities: ['has_card'], homeId: ids.locationSunkenChapelCrypt.id }),
        sheetId: null,
    },
};
/** A card write the rows refuse (2.M s1 deliverable 6): `details.path` and `details.issue`. */
const cardInvalid = (issue, path) => ({
    status: 422,
    body: { code: 'invalid_body', message: 'The card cannot be written.', details: { path, issue } },
});
/**
 * The card writes' declared refusals (CS r6c deliverable 202), one example of each: a second card
 * or a stale `baseRv` 409 `row_conflict`; a trashed page 409 `document_trashed`; and each 422
 * `issue`. Requests name demo rows where one meets the refusal: a quest page has no card, and an
 * item page refuses spell data.
 */
export const EXAMPLE_CARD_REFUSALS = {
    'rules.create': [
        {
            status: 409,
            body: { code: 'row_conflict', message: 'The page already has a card.' },
        },
        trashed,
        {
            ...cardInvalid('type_without_card', ['params', 'id']),
            request: {
                params: { id: ids.questSaltInTheWells.id },
                body: { kind: 'item', data: EXAMPLE_CARD_ITEM },
            },
        },
        {
            ...cardInvalid('kind_mismatch', ['body', 'kind']),
            request: {
                params: { id: ids.itemLanternOfWarding.id },
                body: { kind: 'spell', data: EXAMPLE_CARD_SPELL },
            },
        },
        {
            ...cardInvalid('rules_2024', ['body', 'data', 'rules']),
            request: {
                params: { id: ids.itemLanternOfWarding.id },
                body: { kind: 'item', data: { ...EXAMPLE_CARD_ITEM, rules: '2024' } },
            },
        },
        {
            ...cardInvalid('schema_invalid', ['body', 'data', 'name']),
            request: {
                params: { id: ids.itemLanternOfWarding.id },
                body: { kind: 'item', data: { ...EXAMPLE_CARD_ITEM, name: 7 } },
            },
        },
    ],
    'rules.update': [
        {
            status: 409,
            body: {
                code: 'row_conflict',
                message: 'The card was changed elsewhere.',
                details: { current: { rv: 2 } },
            },
        },
        trashed,
        cardInvalid('kind_mismatch', ['body', 'data']),
        cardInvalid('rules_2024', ['body', 'data', 'rules']),
        cardInvalid('schema_invalid', ['body', 'data']),
    ],
};
//# sourceMappingURL=examples-reference.js.map